import { z } from "zod";
import { IdSchema, PositionSchema } from "./primitives";

/** A rectangle on the map, by its south-west and north-east corners. */
export const BoundsSchema = z.strictObject({
  southwest: PositionSchema,
  northeast: PositionSchema,
});
export type Bounds = z.infer<typeof BoundsSchema>;

/** A rectangle on one canvas: what the map should show, fitted to each screen as best it can. */
export const ExtentSchema = BoundsSchema.extend({ canvasId: IdSchema });
export type Extent = z.infer<typeof ExtentSchema>;

const RepeatingEdgesSchema = z.strictObject({ type: z.literal("repeating") }).meta({
  description: "The canvas goes on past its east and west edges, as copies of itself.",
});

const OpenEdgesSchema = z.strictObject({ type: z.literal("open") }).meta({
  description: "One copy of the canvas, which the map may look past.",
});

const BoundedEdgesSchema = z.strictObject({
  type: z.literal("bounded"),
  bounds: BoundsSchema.refine(
    ({ southwest, northeast }) => southwest[0] < northeast[0] && southwest[1] < northeast[1],
    "The south-west corner must lie south and west of the north-east corner.",
  ).meta({ description: "All of the canvas there is. The map stays over it." }),
});

/** Where a flat canvas ends. A bounded canvas never repeats. */
export const FlatEdgesSchema = z.discriminatedUnion("type", [
  RepeatingEdgesSchema,
  OpenEdgesSchema,
  BoundedEdgesSchema,
]);
export type FlatEdges = z.infer<typeof FlatEdgesSchema>;

/** How a canvas is drawn. A globe has no edge, so only a flat canvas alone can be bounded. */
export const DisplaySchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("globe") }),
  z.strictObject({ type: z.literal("flat"), edges: FlatEdgesSchema }),
  z.strictObject({
    type: z.literal("globeAndFlat"),
    shownFirst: z.enum(["globe", "flat"]),
    edges: z.discriminatedUnion("type", [RepeatingEdgesSchema, OpenEdgesSchema]),
  }),
]);
export type Display = z.infer<typeof DisplaySchema>;

/** The bounds of a canvas drawn as a bounded flat canvas, and nothing for any other. */
export function displayBounds(display: Display): Bounds | undefined {
  return display.type === "flat" && display.edges.type === "bounded"
    ? display.edges.bounds
    : undefined;
}
